class TorvaHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.torva-hero-track');
    this.slides = this.querySelectorAll('.torva-hero-slide');
    this.prevButton = this.querySelector('.torva-hero-arrow--prev');
    this.nextButton = this.querySelector('.torva-hero-arrow--next');
    this.dots = this.querySelectorAll('.torva-hero-dot');
    this.infinite = this.dataset.infinite === 'true';
    this.isJumping = false;

    this.resizeObserver = new ResizeObserver(this.debounce(() => {
      this.initHeaderHeight();
      this.updateControls();
    }, 100));

    if (document.body) {
        this.resizeObserver.observe(document.body);
    }

    this.initHeaderHeight();

    if (this.infinite && this.slides.length > 1) {
      this.setupInfinite();
    }

    this.bindEvents();
    window.requestAnimationFrame(() => this.updateControls());
  }

  setupInfinite() {
    const slides = Array.from(this.slides);
    this.realSlideCount = slides.length;

    const lastClone = slides[slides.length - 1].cloneNode(true);
    lastClone.setAttribute('aria-hidden', 'true');
    this.track.insertBefore(lastClone, this.track.firstChild);

    const firstClone = slides[0].cloneNode(true);
    firstClone.setAttribute('aria-hidden', 'true');
    this.track.appendChild(firstClone);

    this.slides = this.querySelectorAll('.torva-hero-slide');

    // Start at the real first slide (index 1, after the prepended clone)
    requestAnimationFrame(() => {
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = this.slideWidth + this.gap;
      requestAnimationFrame(() => { this.track.style.scrollSnapType = ''; });
    });
  }

  initHeaderHeight() {
    const header = document.querySelector('header-wrapper') || document.querySelector('sticky-header') || document.querySelector('header') || document.querySelector('.header');
    const headerHeight = header ? header.offsetHeight : 0;
    this.style.setProperty('--header-height', `${headerHeight}px`);
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollBySlide(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollBySlide(1));
    }

    if (this.dots.length > 0) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.scrollToSlide(this.infinite ? index + 1 : index);
        });
      });
    }

    this.track.addEventListener('scroll', this.debounce(() => this.updateControls(), 100));

    let isDown = false;
    let isDragging = false;
    let startX;
    let scrollLeft;
    let lastX;

    const startDrag = (e) => {
      isDown = true;
      isDragging = false;
      startX = e.pageX || e.touches?.[0]?.pageX;
      lastX = startX;
      scrollLeft = this.track.scrollLeft;
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      this.track.classList.remove('is-dragging');

      if (isDragging) {
        const slideSize = this.slideWidth + this.gap;
        const walkX = lastX - startX;
        const threshold = slideSize * 0.15;

        let newIndex = Math.round(this.track.scrollLeft / slideSize);
        if (Math.abs(walkX) > threshold) {
          newIndex = walkX < 0 ? Math.ceil(this.track.scrollLeft / slideSize) : Math.floor(this.track.scrollLeft / slideSize);
        }

        this.scrollToSlide(newIndex);
      }
    };

    const moveDrag = (e) => {
      if (!isDown) return;
      lastX = e.pageX || e.touches?.[0]?.pageX;
      const walk = (lastX - startX) * 1.2; 
      if (Math.abs(walk) > 10) {
        isDragging = true;
        this.track.classList.add('is-dragging');
        if(e.cancelable) {
          e.preventDefault();
        }
      }
      this.track.scrollLeft = scrollLeft - walk;
    };

    this.track.addEventListener('mousedown', startDrag);
    this.track.addEventListener('touchstart', startDrag, { passive: true });
    this.track.addEventListener('mouseleave', endDrag);
    this.track.addEventListener('mouseup', endDrag);
    this.track.addEventListener('touchend', endDrag);
    this.track.addEventListener('mousemove', moveDrag);
    this.track.addEventListener('touchmove', moveDrag, { passive: false });
    
    this.track.addEventListener('click', (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  get slideWidth() {
    return this.slides[0]?.offsetWidth || 0;
  }

  get gap() {
    const gapStyle = window.getComputedStyle(this.track).columnGap || window.getComputedStyle(this.track).gap;
    return parseInt(gapStyle) || 0;
  }

  scrollBySlide(direction) {
    const currentScroll = this.track.scrollLeft;
    const slideSize = this.slideWidth + this.gap;
    const nextIndex = Math.round(currentScroll / slideSize) + direction;
    this.scrollToSlide(nextIndex);
  }

  scrollToSlide(index) {
    const slideSize = this.slideWidth + this.gap;
    if (this.infinite) {
      this.track.scrollTo({ left: index * slideSize, behavior: 'smooth' });
    } else {
      const targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({ left: targetIndex * slideSize, behavior: 'smooth' });
    }
  }

  handleInfiniteJump() {
    if (this.isJumping) return;
    const slideSize = this.slideWidth + this.gap;
    const totalSlides = this.slides.length;
    const currentIndex = Math.round(this.track.scrollLeft / slideSize);

    if (currentIndex === 0) {
      // Landed on prepended clone of last slide — jump to real last slide
      this.isJumping = true;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = (totalSlides - 2) * slideSize;
      requestAnimationFrame(() => {
        this.track.style.scrollSnapType = '';
        this.isJumping = false;
      });
    } else if (currentIndex === totalSlides - 1) {
      // Landed on appended clone of first slide — jump to real first slide
      this.isJumping = true;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = slideSize;
      requestAnimationFrame(() => {
        this.track.style.scrollSnapType = '';
        this.isJumping = false;
      });
    }
  }

  updateControls() {
    if (!this.slides.length) return;
    const scrollLeft = this.track.scrollLeft;
    const slideSize = this.slideWidth + this.gap;
    const activeIndex = Math.round(scrollLeft / slideSize);

    if (this.infinite) {
      // Always show arrows
      if (this.prevButton) { this.prevButton.style.opacity = '1'; this.prevButton.disabled = false; }
      if (this.nextButton) { this.nextButton.style.opacity = '1'; this.nextButton.disabled = false; }

      // Map clone indices back to real dot index
      const realCount = this.realSlideCount;
      let realIndex = activeIndex - 1;
      if (realIndex < 0) realIndex = realCount - 1;
      if (realIndex >= realCount) realIndex = 0;

      if (this.dots.length > 0) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === realIndex);
          if (i === realIndex) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      this.handleInfiniteJump();
    } else {
      if (this.prevButton) {
        this.prevButton.style.opacity = activeIndex === 0 ? '0' : '1';
        this.prevButton.disabled = activeIndex === 0;
      }
      if (this.nextButton) {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const isAtEnd = scrollLeft >= maxScroll - 10;
        this.nextButton.style.opacity = isAtEnd ? '0' : '1';
        this.nextButton.disabled = isAtEnd;
      }
      if (this.dots.length > 0) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activeIndex);
          if (i === activeIndex) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('torva-hero-slider')) {
    customElements.define('torva-hero-slider', TorvaHeroSlider);
}

class TorvaProductGallery extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.torva-product-gallery__track');
    this.slides = this.querySelectorAll('.torva-product-gallery__slide');
    this.dots = this.querySelectorAll('.torva-product-gallery__dot');

    if (!this.track || this.slides.length <= 1) return;

    this.bindEvents();
    window.requestAnimationFrame(() => this.updateControls());
  }

  bindEvents() {
    if (this.dots.length > 0) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToSlide(index));
      });
    }

    this.track.addEventListener('scroll', this.debounce(() => this.updateControls(), 100));

    let isDown = false;
    let isDragging = false;
    let startX;
    let scrollLeft;
    let lastX;

    const startDrag = (e) => {
      isDown = true;
      isDragging = false;
      startX = e.pageX || e.touches?.[0]?.pageX;
      lastX = startX;
      scrollLeft = this.track.scrollLeft;
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      this.track.classList.remove('is-dragging');
      
      if (isDragging) {
        const slideSize = this.slideWidth;
        const walkX = lastX - startX;
        const threshold = slideSize * 0.15; 
        
        let newIndex = Math.round(this.track.scrollLeft / slideSize);
        if (Math.abs(walkX) > threshold) {
          newIndex = walkX < 0 ? Math.ceil(this.track.scrollLeft / slideSize) : Math.floor(this.track.scrollLeft / slideSize);
        }
        
        this.scrollToSlide(newIndex);
      }
    };

    const moveDrag = (e) => {
      if (!isDown) return;
      lastX = e.pageX || e.touches?.[0]?.pageX;
      const walk = (lastX - startX) * 1.2; 
      if (Math.abs(walk) > 10) {
        isDragging = true;
        this.track.classList.add('is-dragging');
        if(e.cancelable) {
          e.preventDefault();
        }
      }
      this.track.scrollLeft = scrollLeft - walk;
    };

    this.track.addEventListener('mousedown', startDrag);
    this.track.addEventListener('touchstart', startDrag, { passive: true });
    this.track.addEventListener('mouseleave', endDrag);
    this.track.addEventListener('mouseup', endDrag);
    this.track.addEventListener('touchend', endDrag);
    this.track.addEventListener('mousemove', moveDrag);
    this.track.addEventListener('touchmove', moveDrag, { passive: false });
    
    this.track.addEventListener('click', (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  get slideWidth() {
    return this.slides[0]?.offsetWidth || 0;
  }

  scrollToSlide(index) {
    const scrollPosition = Math.max(0, Math.min(index, this.slides.length - 1)) * this.slideWidth;
    this.track.scrollTo({ left: scrollPosition, behavior: 'smooth' });
  }

  updateControls() {
    if (!this.slides.length || this.dots.length === 0) return;
    const scrollLeft = this.track.scrollLeft;
    const slideSize = this.slideWidth;
    const activeIndex = Math.round(scrollLeft / slideSize);

    this.dots.forEach((dot, i) => {
      if (i === activeIndex) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      }
    });
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('torva-product-gallery')) {
  customElements.define('torva-product-gallery', TorvaProductGallery);
}

class TorvaTestimonialsSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.torva-testimonials__track');
    this.slides = this.querySelectorAll('.torva-testimonials__item');
    if (!this.track || this.slides.length <= 1) return;
    this.bindEvents();
  }

  bindEvents() {
    let isDown = false;
    let isDragging = false;
    let startX;
    let scrollLeft;
    let lastX;

    const startDrag = (e) => {
      isDown = true;
      isDragging = false;
      startX = e.pageX || e.touches?.[0]?.pageX;
      lastX = startX;
      scrollLeft = this.track.scrollLeft;
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      this.track.classList.remove('is-dragging');
      
      if (isDragging) {
        const slideSize = this.slides[0].offsetWidth + 20; // 20px is gap
        const walkX = lastX - startX;
        const threshold = 50; 
        
        let newIndex = Math.round(this.track.scrollLeft / slideSize);
        if (Math.abs(walkX) > threshold) {
          newIndex = walkX < 0 ? Math.ceil(this.track.scrollLeft / slideSize) : Math.floor(this.track.scrollLeft / slideSize);
        }
        
        this.track.scrollTo({ 
          left: Math.max(0, Math.min(newIndex, this.slides.length - 1)) * slideSize, 
          behavior: 'smooth' 
        });
      }
    };

    const moveDrag = (e) => {
      if (!isDown) return;
      lastX = e.pageX || e.touches?.[0]?.pageX;
      const walk = (lastX - startX) * 1.2; 
      if (Math.abs(walk) > 10) {
        isDragging = true;
        this.track.classList.add('is-dragging');
        if(e.cancelable) {
          e.preventDefault();
        }
      }
      this.track.scrollLeft = scrollLeft - walk;
    };

    this.track.addEventListener('mousedown', startDrag);
    this.track.addEventListener('touchstart', startDrag, { passive: true });
    this.track.addEventListener('mouseleave', endDrag);
    this.track.addEventListener('mouseup', endDrag);
    this.track.addEventListener('touchend', endDrag);
    this.track.addEventListener('mousemove', moveDrag);
    this.track.addEventListener('touchmove', moveDrag, { passive: false });
  }
}

if (!customElements.get('torva-testimonials-slider')) {
  customElements.define('torva-testimonials-slider', TorvaTestimonialsSlider);
}
